/**
 * PilotPanel — Text-only chat UI for the Pilot orchestrator.
 *
 * Shown by default on the SP page. Guides the user through the 7-step
 * workflow, dispatches step_workspace_open_requested events that switch
 * the left pane to the CFA for a specific step.
 */
import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePilotChat, type PilotMessage, type StepOpenEvent } from '../hooks/usePilotChat';

interface PilotPanelProps {
  subProjectId: string;
  subProjectName: string;
  kbId?: string;
  kbName?: string;
  solutionId?: string | null;
  language: string;
  onStepOpen: (event: StepOpenEvent) => void;
  infoButton?: React.ReactNode;
}

const PilotPanel: React.FC<PilotPanelProps> = ({
  subProjectId,
  subProjectName,
  kbId,
  kbName,
  solutionId,
  language,
  onStepOpen,
  infoButton,
}) => {
  const { t } = useTranslation();
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [inputText, setInputText] = useState('');

  const {
    messages,
    isReady,
    isConnecting,
    isProcessing,
    sendMessage,
    updateSolution,
  } = usePilotChat({
    subProjectId,
    subProjectName,
    kbId,
    kbName,
    solutionId,
    language,
    onStepOpen,
  });

  // Update Pilot when solution changes
  useEffect(() => {
    updateSolution(solutionId ?? null);
  }, [solutionId, updateSolution]);

  // Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputText.trim() && !isProcessing) {
      sendMessage(inputText.trim());
      setInputText('');
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 16v-4M12 8h.01"/>
          </svg>
          <span className="text-sm font-semibold text-slate-700">{t('pilot.title', 'Pilot')}</span>
          {infoButton}
        </div>
        <span className="text-xs text-slate-400">
          {isConnecting && t('pilot.connecting', 'Connecting...')}
          {isReady && !isProcessing && t('pilot.ready', 'Ready')}
          {isProcessing && t('pilot.thinking', 'Thinking...')}
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 min-h-0">
        {messages.length === 0 && isReady && (
          <div className="text-center py-6 text-sm text-slate-400">
            {t('pilot.emptyState', 'Ask the Pilot to guide you through the workflow steps.')}
          </div>
        )}

        {messages.map((msg: PilotMessage, idx: number) => (
          <div
            key={idx}
            className={`text-sm rounded px-3 py-2 max-w-[85%] whitespace-pre-wrap ${
              msg.role === 'user'
                ? 'ml-auto bg-blue-50 text-slate-800'
                : 'mr-auto bg-slate-100 text-slate-700'
            }`}
          >
            {msg.content}
          </div>
        ))}

        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 border-t border-slate-200 px-3 py-2 bg-white">
        <form onSubmit={handleSubmit} className="flex gap-2 items-end">
          <textarea
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e as React.FormEvent); } }}
            placeholder={t('pilot.placeholder', 'Ask the Pilot...')}
            disabled={!isReady || isProcessing}
            rows={1}
            style={{ resize: 'none', overflowY: 'hidden', minHeight: 32, maxHeight: 120, fieldSizing: 'content' } as React.CSSProperties}
            className="flex-1 text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:border-blue-400"
          />
          <button
            type="submit"
            disabled={!inputText.trim() || !isReady || isProcessing}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded disabled:opacity-40 hover:bg-blue-700 flex-shrink-0"
          >
            {t('pilot.send', 'Send')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PilotPanel;
